import { supabase } from "@/integrations/supabase/client";

type ErrorType = "client" | "edge_function" | "tracking" | "import";

interface ErrorContext {
  [key: string]: unknown;
}

interface ErrorDetails {
  /** Error code for categorization */
  errorCode?: string;
  /** Stack trace from the error */
  stackTrace?: string;
  /** Additional context data */
  context?: ErrorContext;
  /** Session ID for guest tracking */
  sessionId?: string;
  /** Page path where error occurred */
  pagePath?: string;
  /** Component/screen name where error originated */
  component?: string;
  /** User action being performed (e.g., "save_course", "import_json") */
  action?: string;
  /** Function or method name that failed */
  functionName?: string;
  /** Module or file path */
  module?: string;
  /** Code snippet or expression that failed */
  codeSnippet?: string;
  /** Additional metadata */
  metadata?: Record<string, unknown>;
}

/**
 * Parse stack trace to extract useful information
 */
function parseStackTrace(stack?: string): {
  functionName?: string;
  module?: string;
  lineInfo?: string;
} {
  if (!stack) return {};
  
  const lines = stack.split("\n");
  // Find first meaningful line (skip "Error:" line)
  for (const line of lines) {
    const trimmed = line.trim();
    if (trimmed.startsWith("at ")) {
      // Extract function name and file info
      const match = trimmed.match(/at\s+([^\s(]+)\s*\(?([^)]+)?\)?/);
      if (match) {
        const [, fnName, location] = match;
        let module: string | undefined;
        let lineInfo: string | undefined;
        
        if (location) {
          // Extract file path from URL
          const fileMatch = location.match(/\/([^/]+\.tsx?|[^/]+\.jsx?)[:\d]*/);
          if (fileMatch) {
            module = fileMatch[1];
            // Extract line:col
            const lineMatch = location.match(/:(\d+:\d+)/);
            lineInfo = lineMatch ? lineMatch[1] : undefined;
          }
        }
        
        return {
          functionName: fnName !== "eval" ? fnName : undefined,
          module,
          lineInfo,
        };
      }
    }
  }
  return {};
}

/**
 * Get browser and device info
 */
function getDeviceInfo(): Record<string, unknown> {
  return {
    viewport: `${window.innerWidth}x${window.innerHeight}`,
    screenSize: `${screen.width}x${screen.height}`,
    colorScheme: window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light",
    online: navigator.onLine,
    language: navigator.language,
    platform: navigator.platform,
  };
}

/**
 * Log an error to the internal_errors table via edge function.
 * This is fire-and-forget - errors are logged silently.
 */
export async function logError(
  errorType: ErrorType,
  message: string,
  options?: ErrorDetails
): Promise<void> {
  try {
    const sessionId = options?.sessionId ?? localStorage.getItem("guest_session_id") ?? undefined;
    
    // Parse stack trace for additional info
    const stackInfo = parseStackTrace(options?.stackTrace);
    
    // Build enriched context with all available details
    const enrichedContext: ErrorContext = {
      // Preserved original context
      ...options?.context,
      
      // Location info
      component: options?.component,
      module: options?.module || stackInfo.module,
      functionName: options?.functionName || stackInfo.functionName,
      lineInfo: stackInfo.lineInfo,
      
      // Action info
      action: options?.action,
      codeSnippet: options?.codeSnippet,
      
      // Additional metadata
      metadata: options?.metadata,
      
      // Device/browser info
      device: getDeviceInfo(),
      
      // Timing
      timestamp: new Date().toISOString(),
      url: window.location.href,
      referrer: document.referrer || undefined,
    };
    
    // Clean up undefined values
    Object.keys(enrichedContext).forEach((key) => {
      if (enrichedContext[key] === undefined) {
        delete enrichedContext[key];
      }
    });
    
    await supabase.functions.invoke("log-error", {
      body: {
        error_type: errorType,
        error_code: options?.errorCode,
        message,
        stack_trace: options?.stackTrace,
        context: Object.keys(enrichedContext).length > 0 ? enrichedContext : undefined,
        session_id: sessionId,
        page_path: options?.pagePath ?? window.location.pathname,
        user_agent: navigator.userAgent,
      },
    });
  } catch (e) {
    // Silent fail - don't want error logging to cause more errors
    console.error("[errorTracking] Failed to log error:", e);
  }
}

/**
 * Create a scoped error logger for a specific component/module
 */
export function createErrorLogger(component: string, module?: string) {
  return {
    log: (
      errorType: ErrorType,
      message: string,
      options?: Omit<ErrorDetails, "component" | "module">
    ) => logError(errorType, message, { ...options, component, module }),
    
    logAction: (
      action: string,
      error: Error | unknown,
      metadata?: Record<string, unknown>
    ) => {
      const err = error instanceof Error ? error : new Error(String(error));
      return logError("client", err.message, {
        component,
        module,
        action,
        stackTrace: err.stack,
        metadata,
      });
    },
  };
}

/**
 * Check if an error is a chunk loading error (recoverable by refresh)
 */
function isChunkLoadingError(message: string): boolean {
  const patterns = [
    "Failed to fetch dynamically imported module",
    "Loading chunk",
    "Loading CSS chunk",
    "error loading dynamically imported module",
    "Importing a module script failed",
    "ChunkLoadError",
  ];
  const lowerMessage = message.toLowerCase();
  return patterns.some((pattern) => lowerMessage.includes(pattern.toLowerCase()));
}

/**
 * Check if an error is environmental/expected and should be suppressed.
 * These are non-actionable errors from browser extensions, 3rd-party scripts,
 * or transient network conditions.
 */
function isEnvironmentalError(message: string, filename?: string): boolean {
  const lowerMessage = message.toLowerCase();
  const lowerFilename = (filename || "").toLowerCase();
  
  // Browser extension errors
  if (
    lowerFilename.includes("extension://") ||
    lowerFilename.includes("chrome-extension") ||
    lowerFilename.includes("moz-extension") ||
    lowerMessage.includes("extension context")
  ) {
    return true;
  }
  
  // 3rd-party script errors (analytics, ads, etc.)
  const thirdPartyPatterns = [
    "flock.js", "gtag", "gtm.js", "analytics", "facebook", "twitter",
    "linkedin", "hotjar", "intercom", "crisp", "drift", "hubspot",
  ];
  if (thirdPartyPatterns.some(p => lowerFilename.includes(p) || lowerMessage.includes(p))) {
    return true;
  }
  
  // Known browser quirks / non-critical DOM errors
  const browserQuirks = [
    "removechild", "insertbefore", "appendchild",
    "script error", "script error.",
    "resizeobserver loop",
    "non-error promise rejection",
    "null is not an object",
    "cannot read properties of null",
    "object not found matching id",
    "methodname:update",
  ];
  if (browserQuirks.some(p => lowerMessage.includes(p))) {
    return true;
  }
  
  // Network/transient errors that aren't actionable
  const transientPatterns = [
    "failed to fetch", "networkerror", "network request failed",
    "err_network", "err_internet_disconnected", "err_connection",
    "timeout", "timed out", "aborterror", "aborted", "load failed",
    "failed to send", "fetch failed", "request failed",
  ];
  if (transientPatterns.some(p => lowerMessage.includes(p))) {
    return true;
  }
  
  // Vite dev server pings (only in development)
  if (lowerMessage.includes("vite") || lowerMessage.includes("text/x-vite")) {
    return true;
  }
  
  return false;
}

/**
 * Capture and log JavaScript errors globally.
 * Call this once at app initialization.
 */
export function initGlobalErrorCapture(): void {
  // Capture unhandled errors
  window.addEventListener("error", (event) => {
    // Skip chunk loading errors - these are handled by lazyWithRetry
    if (isChunkLoadingError(event.message)) {
      return;
    }
    
    // Skip environmental/expected errors - not actionable
    if (isEnvironmentalError(event.message, event.filename)) {
      return;
    }
    
    const stackInfo = parseStackTrace(event.error?.stack);
    
    logError("client", event.message, {
      stackTrace: event.error?.stack,
      module: stackInfo.module || event.filename?.split("/").pop(),
      functionName: stackInfo.functionName,
      context: {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        lineInfo: `${event.lineno}:${event.colno}`,
      },
    });
  });

  // Capture unhandled promise rejections
  window.addEventListener("unhandledrejection", (event) => {
    const message = event.reason?.message || String(event.reason);
    
    // Skip chunk loading errors - these are handled by lazyWithRetry
    if (isChunkLoadingError(message)) {
      return;
    }
    
    // Skip environmental/expected errors - not actionable
    if (isEnvironmentalError(message)) {
      return;
    }
    
    const stackInfo = parseStackTrace(event.reason?.stack);
    
    logError("client", `Unhandled Promise Rejection: ${message}`, {
      stackTrace: event.reason?.stack,
      module: stackInfo.module,
      functionName: stackInfo.functionName,
      context: {
        type: "unhandledrejection",
        reason: typeof event.reason === "object" ? event.reason?.name : typeof event.reason,
      },
    });
  });
}
